import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Space, Flex, Select, Input, Button, Divider, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs'; 
import { lookupMenuItems } from '../../services/menuservice';
import { MenuItemLookup } from '../../types/menutypes';
import debounce from 'lodash/debounce';
import { useAuth } from '../../auth/authcontext';

const { Option } = Select;

interface AddOnEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    menuAddOnDropDown: { key: string; name: string; upc: string; itemType?: number }[];
    selectedMenuAddOnGuid: string;
    handleAddOnChange: (value: string) => void;
    isWarnMenuAddOn: boolean;
    selectedAddOnDisplayName: string;
    handleAddOnDisplayTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    isWarnAddOnDisplayName: boolean;
    handleSaveAddOnLink: () => void;
    handleCancelAddOnLink: () => void;
    selectedAddOnStartDate: Dayjs | null;
    selectedAddOnEndDate: Dayjs | null;
    handleAddOnStartDateChange: (date: Dayjs | null) => void;
    handleAddOnEndDateChange: (date: Dayjs | null) => void;
}

const WarningMsg: React.FC<{ msg: string }> = ({ msg }) => (
    <span style={{ color: 'red' }}>{msg}</span>
);

const AddOnEditorDrawer: React.FC<AddOnEditorDrawerProps> = ({
    open,
    onClose,
    menuAddOnDropDown,
    selectedMenuAddOnGuid,
    handleAddOnChange,
    isWarnMenuAddOn,
    selectedAddOnDisplayName,
    handleAddOnDisplayTextChange,
    isWarnAddOnDisplayName,
    handleSaveAddOnLink,
    handleCancelAddOnLink,
    selectedAddOnStartDate,
    selectedAddOnEndDate,
    handleAddOnStartDateChange,
    handleAddOnEndDateChange,
}) => {
    const { isReadOnly } = useAuth();
    const [source, setSource] = useState<"menu" | "product" | null>(null);
    const [items, setItems] = useState<MenuItemLookup[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);

    useEffect(() => {
        if (open) {
            if (selectedMenuAddOnGuid && selectedMenuAddOnGuid !== "(SELECT)") {
                const matchedAddOn = menuAddOnDropDown.find(o => o.key === selectedMenuAddOnGuid);
                if (matchedAddOn) {
                    const inferredSource = matchedAddOn.itemType === 0 ? "product" : "menu";
                    setSource(inferredSource);
                    setItems([{
                        id: inferredSource === "product" ? matchedAddOn.upc : matchedAddOn.key,
                        menuGuid: matchedAddOn.key,
                        name: matchedAddOn.name
                    }]);
                }
            } else {
                setSource(null);
                setItems([]);
            }
        }
    }, [open, selectedMenuAddOnGuid, menuAddOnDropDown]);

    const fetchLookupItems = async (query = "", selectedSource = source) => {
        if (!selectedSource) return;
        setLoadingItems(true);
        try {
            const res = await lookupMenuItems(selectedSource, query, 50);
            setItems(res.items);
        } catch (error) {
            console.error("Failed to load lookup items", error);
        } finally {
            setLoadingItems(false);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce((value: string) => fetchLookupItems(value), 300),
        [source]
    );

    const handleSourceChange = (val: "menu" | "product") => {
        setSource(val);
        setItems([]);
        handleAddOnChange("");
        fetchLookupItems("", val);
    };

    const handleSelectItem = (menuGuid: string) => {
        handleAddOnChange(menuGuid);
        const selectedItem = items.find(i => i.menuGuid === menuGuid);
        if (selectedItem && !selectedAddOnDisplayName) {
            const fakeEvent = { target: { value: selectedItem.name } } as React.ChangeEvent<HTMLInputElement>;
            handleAddOnDisplayTextChange(fakeEvent);
        }
    };

    return (
        <Drawer open={open} onClose={onClose} placement="right" title="Edit Add-On">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Flex gap="bottom" vertical>
                    <label className="form-label">Source:</label>
                    <Select
                        placeholder="Select source"
                        value={source || undefined}
                        onChange={handleSourceChange}
                        style={{ width: '100%' }}
                    >
                        <Option value="menu">Menu</Option>
                        <Option value="product">Product</Option>
                    </Select>
                </Flex>

                <Flex gap="bottom" vertical>
                    <label className="form-label">Add-On:</label>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Search Add-On by name or POS"
                        allowClear
                        value={selectedMenuAddOnGuid || undefined}
                        disabled={!source}
                        loading={loadingItems}
                        filterOption={false}
                        onSearch={debouncedSearch}
                        onDropdownVisibleChange={(open) => {
                            if (open && items.length === 0 && source) {
                                fetchLookupItems("");
                            }
                        }}
                        onChange={handleSelectItem}
                    >
                        <Option value="(SELECT)">(SELECT)</Option>
                        {items.map((option) => (
                            <Option key={option.menuGuid} value={option.menuGuid}>
                                {option.name}
                            </Option>
                        ))}
                    </Select>
                    {isWarnMenuAddOn && <WarningMsg msg="Add-on is Required" />}
                </Flex>

            <Flex gap="bottom" vertical>
                <label className="form-label">Kiosk Text:</label>
                <Input
                    value={selectedAddOnDisplayName}
                    placeholder="Display Name"
                    onChange={handleAddOnDisplayTextChange}
                    style={{
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderRadius: '8px 0 0 8px',
                        width: '100%',
                        fontSize: '14px',
                    }}
                />
                {isWarnAddOnDisplayName && <WarningMsg msg="Kiosk Text is Required" />}
            </Flex>
            <Divider />
                <Flex gap="bottom" vertical>
                        <label>Optional dates for seasonal items, or new upcoming items that will be shown for a limited time.  Leave dates blank for add-ons that should be included year round.</label>
                </Flex>
            <Flex gap="bottom" vertical>
                    
                    <label className="form-label">Start Date:</label>
                    <DatePicker
                        showTime={false}
                        format="YYYY-MM-DD"
                        style={{ width: '100%' }}
                        value={selectedAddOnStartDate ? dayjs(selectedAddOnStartDate) : null}
                        onChange={handleAddOnStartDateChange}
                        allowClear={true}
                    />
            </Flex>
            <Flex gap="bottom" vertical>
                    <label className="form-label">End Date:</label>
                <DatePicker
                showTime={false}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                // Wrap the value in dayjs() if it exists
                value={selectedAddOnEndDate ? dayjs(selectedAddOnEndDate) : null}
                    allowClear={true}
                onChange={handleAddOnEndDateChange}

                />
            </Flex>
            <Flex>
                <Space direction="horizontal" size="middle">
                    {!isReadOnly && (
                        <Button
                            color="green"
                            type="primary"
                            variant="solid"
                            size="small"
                            style={{
                                marginTop: '20px',
                                paddingInline: '15px',
                                fontSize: '12px',
                                width: '100px',
                            }}
                            onClick={handleSaveAddOnLink}
                        >
                            Save Changes
                        </Button>
                    )}
                    <Button
                        variant="solid"
                        size="small"
                        style={{
                            marginTop: '20px',
                            paddingInline: '15px',
                            fontSize: '12px',
                            width: '100px',
                        }}
                        onClick={handleCancelAddOnLink}
                    >
                        Cancel
                    </Button>
                </Space>
            </Flex>
        </Space>
    </Drawer>
    );
};

export default AddOnEditorDrawer;